import { useRef, useCallback, useState, useEffect, ReactElement } from 'react';

import { FormHandles } from '@unform/core';
import { useHistory, useLocation, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import * as Yup from 'yup';

import { GoBackButton, Input, Select, InputRadio, Button } from '~/components';
import { useLoading } from '~/hooks/loading';
import { IPackage } from '~/models/Common';
import ProfessionalService from '~/services/Professional';
import { handleYupValidationError } from '~/utils';

import * as S from './styles';

interface IPackageStatusOption {
  label: string;
  value: string;
}

interface IEditPackageParams {
  id: string;
}

interface IEditPackageRouteState {
  packageData: IPackage;
}

function EditPackage(): ReactElement {
  const { setLoading } = useLoading();

  const [initialData, setInitialData] = useState<IPackage>();
  const formRef = useRef<FormHandles>(null);
  const router = useHistory();
  const packageData = useLocation<IEditPackageRouteState>().state?.packageData;
  const { id } = useParams<IEditPackageParams>();

  if (!packageData) {
    toast.error('Pacote inválido');
    router.goBack();
  }

  const packageStatusOptions: [IPackageStatusOption, IPackageStatusOption] = [
    { label: 'Ativo', value: 'active' },
    { label: 'Inativo', value: 'inactive' },
  ];

  const packageFidelizationOptions = [
    {
      id: 'monthly',
      label: 'Mensal',
      value: 'monthly',
      checked: packageData.lealtad === 'monthly',
    },
    {
      id: 'quarterly',
      label: 'Trimestral',
      value: 'quarterly',
      checked: packageData.lealtad === 'quarterly',
    },
    {
      id: 'semiannual',
      label: 'Semestral',
      value: 'semiannual',
      checked: packageData.lealtad === 'semiannual',
    },
    {
      id: 'annual',
      label: 'Anual',
      value: 'annual',
      checked: packageData.lealtad === 'annual',
    },
  ];

  const handleCancel = useCallback(() => {
    router.goBack();
  }, [router]);

  const PackageSchema = Yup.object().shape({
    name: Yup.string().required(`Nome é um campo obrigatório`),
    value: Yup.number()
      .min(3, (v) => `O valor deve conter no mínimo ${v.min} caracteres`)
      .required(`Valor é um campo obrigatório`),
    lealtad: Yup.string().required(`Fidelização é um campo obrigatório`),
    number_of_training_programs: Yup.string().required(
      `N° de programas de treino é um campo obrigatório`,
    ),
    status: Yup.string().required(`Status é um campo obrigatório`),
  });

  const handlePackageEditSubmit = async (data: IPackage) => {
    try {
      setLoading(true);

      formRef.current?.setErrors({});
      await PackageSchema.validate(data, {
        abortEarly: false,
      });
      const result = await ProfessionalService.editUserPackage(
        packageData.id,
        data,
      );
      if (result.status === 200) {
        toast.success('Pacote editado com sucesso!');
        router.goBack();
      } else {
        toast.success('Ocorreu um erro ao editar o pacote!');
      }

      setLoading(false);
    } catch (error) {
      setLoading(false);
      handleYupValidationError(formRef, error);
    }
  };

  useEffect(() => {
    setInitialData(packageData);
    formRef.current?.setFieldValue(
      'status',
      packageData.status === 'active' ? 'active' : 'inactive',
    );
    formRef.current?.setFieldValue('lealtad', packageData.lealtad);
  }, [packageData]);

  return (
    <>
      <GoBackButton
        containerStyle={{ alignSelf: 'flex-start', paddingTop: '2.2rem' }}
      />
      <S.Container>
        <S.EditPackageHeader>
          <S.EditPackageTitle>Editar pacote</S.EditPackageTitle>
        </S.EditPackageHeader>
        <S.EditPackageForm
          id="edit-package"
          initialData={initialData}
          ref={formRef}
          onSubmit={handlePackageEditSubmit}
        >
          <Input name="name" label="Nome" />
          <Input name="value" label="Valor" type="number" />
          <Input
            type="number"
            name="number_of_training_programs"
            label="N° de programas de treino"
          />
          <S.EditPackageInputBlock
            style={{
              gridRowStart: 2,
              gridRowEnd: 4,
              gridColumnStart: 2,
              gridColumnEnd: 3,
            }}
          >
            <p>Fidelização</p>
            <InputRadio name="lealtad" options={packageFidelizationOptions} />
          </S.EditPackageInputBlock>
          <S.EditPackageInputBlock>
            <p>Status</p>
            <Select name="status" options={packageStatusOptions} />
          </S.EditPackageInputBlock>
        </S.EditPackageForm>
        <S.EditPackageFooter>
          <Button
            form="edit-package"
            type="submit"
            textContent="Salvar"
            variant="black"
            size="small"
            extraStyles={{
              maxWidth: '200px',
            }}
          />
          <Button
            type="button"
            onClick={handleCancel}
            textContent="Cancelar"
            variant="red"
            size="small"
            extraStyles={{
              maxWidth: '200px',
            }}
          />
        </S.EditPackageFooter>
      </S.Container>
    </>
  );
}

export default EditPackage;
